import { useMemo } from 'react';
import { toast as reactToast } from 'react-toastify';
import { ToastMessage } from '@/components/ui/toast/toast-message';
import {
  getToastOptions,
  DEFAULT_AUTO_CLOSE_DURATION,
  type ToastOptions,
} from '@/utils/toast';

export type { ToastOptions };

type ToastType = 'success' | 'error' | 'warning' | 'info';

const showToast = (
  type: ToastType,
  options: ToastOptions,
  defaultAutoClose: number | false
): void => {
  const opts = {
    ...getToastOptions(options, defaultAutoClose),
    className: `toast-${type}`,
  };
  reactToast[type](<ToastMessage options={options} />, opts);
};

/**
 * React hook for showing toasts with stable reference
 * Returns a memoized object to prevent unnecessary re-renders
 */
export const useToast = (): {
  /** Show success toast (green) - auto-closes after 4 seconds */
  success: (options: ToastOptions) => void;
  /** Show error toast (red) - stays visible (no auto-close) */
  error: (options: ToastOptions) => void;
  /** Show warning toast (orange) - auto-closes after 4 seconds */
  warning: (options: ToastOptions) => void;
  /** Show info toast (blue) - auto-closes after 4 seconds */
  info: (options: ToastOptions) => void;
  /** Dismiss specific toast */
  dismiss: (toastId?: string | number) => void;
  /** Dismiss all toasts */
  dismissAll: () => void;
} => {
  return useMemo(
    () => ({
      success: (options: ToastOptions) =>
        showToast('success', options, DEFAULT_AUTO_CLOSE_DURATION),

      error: (options: ToastOptions) => showToast('error', options, false),

      warning: (options: ToastOptions) =>
        showToast('warning', options, DEFAULT_AUTO_CLOSE_DURATION),

      info: (options: ToastOptions) =>
        showToast('info', options, DEFAULT_AUTO_CLOSE_DURATION),

      dismiss: (toastId?: string | number) => reactToast.dismiss(toastId),

      dismissAll: () => reactToast.dismiss(),
    }),
    []
  );
};
